import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

interface Module { fichier: string; present: boolean }
interface Piece {
  id: string; nom: string; mur: string | null; etat: string; objectif: string; entree: string; sortie: string; degrade: string; mesure: string;
  modules: Module[]; sondes: Record<string, number>; manque: string[]; modules_manquants: string[];
}
interface Livrable { forfait: string; livrable: string; etat: string; modules: Module[]; lacune: string }
interface Indic { indicateur: string; cible: number; mesure: number | null; source: string; etat: string }
interface Correction { date: string; domaine: string; probleme: string; cause: string; controle: string; statut: string }
interface Maison { fiabilite?: { journal: Correction[]; total: number; couvertes: number; ouvertes: number }; produit_le: string; pieces: Piece[]; livrables: Livrable[]; trajectoire: Indic[]; bilan: Record<string, number>; murs: Record<string, string>; avertissement: string }

const ETAT: Record<string, { libelle: string; classe: string }> = {
  en_service: { libelle: "En service", classe: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  present: { libelle: "Présent", classe: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  partiel: { libelle: "Partiel", classe: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  squelette: { libelle: "Squelette", classe: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  absent: { libelle: "Absent", classe: "bg-red-500/15 text-red-400 border-red-500/30" },
};
const MUR: Record<string, string> = { acquisition: "Mur porteur · acquisition", livraison: "Mur porteur · livraison" };

function PieceCard({ p }: { p: Piece }) {
  const [ouvert, setOuvert] = useState(false);
  const e = ETAT[p.etat] || ETAT.absent;
  return (
    <Card className={p.mur ? "border-amber-500/40" : ""}>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setOuvert(!ouvert)}>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{p.nom}</CardTitle>
          {ouvert ? <ChevronUp className="h-4 w-4 shrink-0 mt-1" /> : <ChevronDown className="h-4 w-4 shrink-0 mt-1" />}
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Badge variant="outline" className={e.classe}>{e.libelle}</Badge>
          {p.mur && <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">{MUR[p.mur]}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">{p.objectif}</p>
        {ouvert && (
          <div className="space-y-2.5">
            <div><b>Entre :</b> {p.entree}</div>
            <div><b>Sort :</b> {p.sortie}</div>
            <div><b>Mode dégradé :</b> {p.degrade}</div>
            <div><b>Se mesure par :</b> {p.mesure}</div>
            {Object.keys(p.sondes).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(p.sondes).map(([k, v]) => <Badge key={k} variant="outline" className="font-normal">{k} : {v.toLocaleString("fr-FR")}</Badge>)}
              </div>
            )}
            {p.modules.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Modules : {p.modules.map((m) => <span key={m.fichier} className={m.present ? "" : "text-red-400"}>{m.fichier}{m.present ? "" : " (absent)"} </span>)}
              </div>
            )}
            {p.manque.length > 0 && (
              <div className="rounded-md bg-muted/40 px-3 py-2">
                <div className="font-medium mb-1">Ce qui manque</div>
                <ul className="list-disc pl-4 space-y-0.5">{p.manque.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Maison() {
  const [m, setM] = useState<Maison | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const r = await fetch(`${BACKEND}/api/maison`, { credentials: "include" });
      if (r.ok) setM(await r.json()); else setErreur(r.status === 403 ? "Réservé au fondateur." : "Chargement impossible.");
    })();
  }, []);
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <main className="flex-1 p-4 md:p-8 space-y-6 max-w-6xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold">La maison de Grafton</h1>
            <p className="text-muted-foreground mt-1">Toutes les pièces d'abord : ce qui entre, ce qui sort, le mode dégradé, et l'état réel mesuré. On s'arrête ensuite dans chaque pièce.</p>
          </div>
          {erreur && <p className="text-red-400">{erreur}</p>}
          {m && (
            <>
              <div className="flex flex-wrap gap-2">
                {Object.entries(m.bilan).map(([k, v]) => <Badge key={k} variant="outline" className={ETAT[k]?.classe}>{ETAT[k]?.libelle} : {v}</Badge>)}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {Object.entries(m.murs).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm"><b>{MUR[k]}.</b> {v}</div>
                ))}
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {m.pieces.map((p) => <PieceCard key={p.id} p={p} />)}
              </div>

              <Card>
                <CardHeader><CardTitle className="text-lg">Livraison des forfaits : ce que le catalogue promet</CardTitle></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 pr-3">Forfait</th><th className="pr-3">Livrable</th><th className="pr-3">État</th><th>Lacune</th></tr></thead>
                    <tbody>
                      {m.livrables.map((l) => (
                        <tr key={l.forfait + l.livrable} className="border-b align-top">
                          <td className="py-2 pr-3 whitespace-nowrap">{l.forfait}</td>
                          <td className="pr-3">{l.livrable}</td>
                          <td className="pr-3"><Badge variant="outline" className={(ETAT[l.etat] || ETAT.absent).classe}>{(ETAT[l.etat] || ETAT.absent).libelle}</Badge></td>
                          <td className="text-muted-foreground">{l.lacune}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-lg">Trajectoire 2027 : ce qui est mesuré</CardTitle></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 pr-3">Indicateur</th><th className="pr-3">Cible</th><th className="pr-3">Mesuré</th><th>Source</th></tr></thead>
                    <tbody>
                      {m.trajectoire.map((t) => (
                        <tr key={t.indicateur} className="border-b">
                          <td className="py-2 pr-3">{t.indicateur}</td>
                          <td className="pr-3">{t.cible.toLocaleString("fr-FR")}</td>
                          <td className="pr-3">{t.mesure === null ? <span className="text-orange-400">non mesuré</span> : t.mesure.toLocaleString("fr-FR")}</td>
                          <td className="text-muted-foreground">{t.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {m.fiabilite && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Fiabilité : les corrections demandées</CardTitle>
                    <p className="text-sm text-muted-foreground">{m.fiabilite.total} correction(s) enregistrée(s) · {m.fiabilite.couvertes} couverte(s) par un contrôle · {m.fiabilite.ouvertes} à traiter. Objectif : zéro reprise pour le dirigeant.</p>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 pr-3">Date</th><th className="pr-3">Problème</th><th className="pr-3">Cause</th><th className="pr-3">Contrôle ajouté</th><th>État</th></tr></thead>
                      <tbody>
                        {m.fiabilite.journal.map((c, i) => (
                          <tr key={i} className="border-b align-top">
                            <td className="py-2 pr-3 whitespace-nowrap">{c.date}</td>
                            <td className="pr-3">{c.probleme}</td>
                            <td className="pr-3 text-muted-foreground">{c.cause}</td>
                            <td className="pr-3 text-muted-foreground">{c.controle}</td>
                            <td><Badge variant="outline" className={c.statut === "controle_en_place" ? ETAT.en_service.classe : ETAT.partiel.classe}>{c.statut === "controle_en_place" ? "Couvert" : "À faire"}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
              <p className="text-xs text-muted-foreground">{m.avertissement}</p>
            </>
          )}
        </main>
      </div>
    </SidebarProvider>
  );
}
